import { bpeConfig } from '@enterprise/common';
import { getMetaData } from 'common/header/notifications/hooks/mappers/impl/shared';
import {
  ExportSource,
  type MappedNotification,
  type NotificationMapper,
  type NotificationMapperOptions,
  NotificationStatusModel
} from 'common/header/notifications/Notifications.types';
import { getInvoiceNotificationTitle, isInvoiceNotification } from 'invoices/notifications/invoiceNotificationUtil';
import { isTimesheetNotification } from 'invoices/notifications/timesheetNotificationUtils';

export const csvExcelExportMapper: NotificationMapper = (notification, options) => {
  return {
    ...notification,
    ...getExportNotificationTitle(notification, options),
    to: `/${bpeConfig.tenant.code}/reports/exports`
  };
};

function getExportNotificationTitle(notification: MappedNotification, options: NotificationMapperOptions) {
  const standardTitle = getTitle(notification, options);

  if (isTimesheetNotification(notification)) {
    return extendWithTimesheetFeatureDescription(notification, options, standardTitle);
  }
  if (isInvoiceNotification(notification)) {
    return {
      ...standardTitle,
      title: getInvoiceNotificationTitle(notification) || standardTitle.title
    };
  }
  return standardTitle;
}

function extendWithTimesheetFeatureDescription(
  notification: MappedNotification,
  options: NotificationMapperOptions,
  standardTitle: ReturnType<typeof getTitle>
) {
  const zeroAllocationCount = getMetaData(notification).numberValue('ZERO_ALLOCATIONS_COUNT');
  if (!standardTitle.title) {
    return standardTitle;
  }

  return {
    ...standardTitle,
    description: `${standardTitle.description} ${getFeatureInfo({
      notification,
      options,
      featureKey: 'EXPORT_TIMESHEET_INCLUDE_EMPTY_ALLOCATION',
      visible: ![NotificationStatusModel.Cancelled].includes(
        notification.status ?? NotificationStatusModel.FinishedSuccess
      ),
      variables: {
        count: options.formatNumber(zeroAllocationCount ?? 0)
      }
    })}`.trimEnd()
  };
}

interface Props {
  notification: MappedNotification;
  options: NotificationMapperOptions;
  featureKey: string;
  visible?: boolean;
  variables?: {
    count?: string;
  };
}

function getFeatureInfo({ notification, featureKey, visible, variables, options }: Props): string {
  const feature = getMetaData(notification).item(featureKey);
  if (!feature?.key || !visible) {
    return '';
  }
  return options.formatExportNotificationMessage.getDescriptionByFeature({
    featureKey: feature.key,
    enabled: feature.value?.toLocaleLowerCase() === 'true',
    count: variables?.count ?? ''
  });
}

function getTitle(notification: MappedNotification, options: NotificationMapperOptions) {
  if (notification.status) {
    const actualCount = getMetaData(notification).numberValue('ACTUAL_COUNT') || 0;
    /**
     * The backend might not send the COUNT metadata, in that case we can use (actualCount || 0) + 1 as an estimation to avoid showing 0/0 - progress 100% in the UI.
     */
    const totalCount = getMetaData(notification).numberValue('COUNT') || actualCount + 1;

    return options.formatExportNotificationMessage.getMessagesByStatus({
      status: notification.status,
      actualCount,
      totalCount,
      prefix: getPrefix(notification, options)
    });
  }
  return options.formatExportNotificationMessage.getTypeName({
    type: notification.type
  });
}

function getPrefix(notification: MappedNotification, options: NotificationMapperOptions) {
  const metaData = getMetaData(notification);
  const name = metaData.value('NAME');
  const sourceType = metaData.value('SOURCE_TYPE');

  const entityName = options.formatExportNotificationMessage.getEntityName({
    entityType: metaData.entityType
  });

  const isCustomObjectExportNotification = metaData.entityType === ExportSource.CustomObjects;

  if (!name) return entityName;
  if (isCustomObjectExportNotification) return name;

  return options.formatExportNotificationMessage.getSourceName({ sourceType, name }) ?? entityName;
}
